import React, { useEffect, useState } from "react";
import * as blogApi from "../../../api/blogApi";
import { getApiErrorMessage } from "../../../utils/apiResponse";
import { resolveMediaUrl } from "../../../utils/mediaUrl";
import { PUBLIC_LANGUAGES } from "../../../utils/languages";

const emptyTranslation = (code) => ({
  languageCode: code,
  category: "",
  title: "",
  excerpt: "",
  body: "",
  authorName: "",
});

const emptyForm = () => ({
  slug: "",
  coverImageUrl: "",
  coverImageAlt: "",
  publishedAtUtc: new Date().toISOString().slice(0, 16),
  isPublished: true,
  isFeatured: false,
  showOnHome: false,
  sortOrder: 0,
  translations: PUBLIC_LANGUAGES.map((l) => emptyTranslation(l.code)),
});

const toLocalInput = (iso) => {
  if (!iso) return new Date().toISOString().slice(0, 16);
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return new Date().toISOString().slice(0, 16);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const BlogFormModal = ({ open, initial, busy, onClose, onSave }) => {
  const [form, setForm] = useState(emptyForm);
  const [langTab, setLangTab] = useState("en");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setLangTab("en");
    if (!initial) {
      setForm(emptyForm());
      return;
    }

    const byLang = Object.fromEntries(
      (initial.translations || []).map((t) => [t.languageCode, t])
    );

    setForm({
      slug: initial.slug || "",
      coverImageUrl: initial.coverImageUrl || "",
      coverImageAlt: initial.coverImageAlt || "",
      publishedAtUtc: toLocalInput(initial.publishedAtUtc),
      isPublished: Boolean(initial.isPublished),
      isFeatured: Boolean(initial.isFeatured),
      showOnHome: Boolean(initial.showOnHome),
      sortOrder: initial.sortOrder ?? 0,
      translations: PUBLIC_LANGUAGES.map((l) => ({
        ...emptyTranslation(l.code),
        ...(byLang[l.code] || {}),
        languageCode: l.code,
      })),
    });
  }, [open, initial]);

  if (!open) return null;

  const activeTranslation =
    form.translations.find((t) => t.languageCode === langTab) || form.translations[0];

  const updateTranslation = (patch) => {
    setForm((prev) => ({
      ...prev,
      translations: prev.translations.map((t) =>
        t.languageCode === langTab ? { ...t, ...patch } : t
      ),
    }));
  };

  const handleCoverUpload = async (file) => {
    if (!file) return;
    setUploadBusy(true);
    setError("");
    try {
      const data = await blogApi.uploadBlogCover(file);
      setForm((prev) => ({ ...prev, coverImageUrl: data.url }));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setUploadBusy(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setError("");

    if (!form.slug.trim() || !form.coverImageUrl.trim()) {
      setError("Slug and cover image are required.");
      return;
    }

    for (const t of form.translations) {
      if (!t.title.trim() || !t.excerpt.trim() || !t.body.trim() || !t.category.trim()) {
        setError(`Complete all fields for ${t.languageCode.toUpperCase()}.`);
        setLangTab(t.languageCode);
        return;
      }
    }

    const published = new Date(form.publishedAtUtc);
    onSave({
      slug: form.slug.trim(),
      coverImageUrl: form.coverImageUrl.trim(),
      coverImageAlt: form.coverImageAlt.trim() || null,
      publishedAtUtc: Number.isNaN(published.getTime())
        ? new Date().toISOString()
        : published.toISOString(),
      isPublished: form.isPublished,
      isFeatured: form.isFeatured,
      showOnHome: form.showOnHome,
      sortOrder: Number(form.sortOrder) || 0,
      translations: form.translations.map((t) => ({
        languageCode: t.languageCode,
        category: t.category.trim(),
        title: t.title.trim(),
        excerpt: t.excerpt.trim(),
        body: t.body.trim(),
        authorName: t.authorName?.trim() || null,
      })),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 sm:p-8">
      <form
        onSubmit={handleSubmit}
        className="my-4 w-full max-w-3xl rounded-3xl border border-slate-200 bg-white shadow-xl"
      >
        <div className="border-b border-slate-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-slate-900">
            {initial ? "Edit blog post" : "New blog post"}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Manage cover image and EN / AR / KU content in one place.
          </p>
        </div>

        <div className="space-y-5 px-6 py-5">
          {error ? (
            <p className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Slug</span>
              <input
                value={form.slug}
                onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-3 py-2"
                placeholder="annual-integrity-report"
                required
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Published at</span>
              <input
                type="datetime-local"
                value={form.publishedAtUtc}
                onChange={(e) => setForm((p) => ({ ...p, publishedAtUtc: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-3 py-2"
              />
            </label>
          </div>

          <div className="rounded-2xl border border-slate-200 p-4">
            <p className="mb-3 text-sm font-semibold text-slate-800">Cover image</p>
            {form.coverImageUrl ? (
              <img
                src={resolveMediaUrl(form.coverImageUrl)}
                alt=""
                className="mb-3 h-40 w-full rounded-xl object-cover"
              />
            ) : null}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <label className="inline-flex cursor-pointer items-center justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
                {uploadBusy ? "Uploading…" : "Upload image"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploadBusy || busy}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    handleCoverUpload(file);
                  }}
                />
              </label>
              <input
                value={form.coverImageUrl}
                onChange={(e) => setForm((p) => ({ ...p, coverImageUrl: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="/uploads/images/..."
              />
            </div>
            <label className="mt-3 block text-sm">
              <span className="mb-1 block text-slate-600">Alt text</span>
              <input
                value={form.coverImageAlt}
                onChange={(e) => setForm((p) => ({ ...p, coverImageAlt: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-3 py-2"
              />
            </label>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isPublished}
                onChange={(e) => setForm((p) => ({ ...p, isPublished: e.target.checked }))}
              />
              Published
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isFeatured}
                onChange={(e) => setForm((p) => ({ ...p, isFeatured: e.target.checked }))}
              />
              Featured
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.showOnHome}
                onChange={(e) => setForm((p) => ({ ...p, showOnHome: e.target.checked }))}
              />
              Show on home
            </label>
            <label className="inline-flex items-center gap-2">
              Sort
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm((p) => ({ ...p, sortOrder: e.target.value }))}
                className="w-20 rounded-xl border border-slate-200 px-2 py-1"
              />
            </label>
          </div>

          <div>
            <div className="mb-3 flex gap-2">
              {PUBLIC_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => setLangTab(lang.code)}
                  className={[
                    "rounded-full px-3 py-1.5 text-xs font-semibold",
                    langTab === lang.code
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-600",
                  ].join(" ")}
                >
                  {lang.short}
                </button>
              ))}
            </div>

            <div className="space-y-3" dir={langTab === "en" ? "ltr" : "rtl"}>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-slate-700">Category</span>
                <input
                  value={activeTranslation.category}
                  onChange={(e) => updateTranslation({ category: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2"
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-slate-700">Title</span>
                <input
                  value={activeTranslation.title}
                  onChange={(e) => updateTranslation({ title: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2"
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-slate-700">Excerpt</span>
                <textarea
                  value={activeTranslation.excerpt}
                  onChange={(e) => updateTranslation({ excerpt: e.target.value })}
                  rows={2}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2"
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-slate-700">
                  Body (paragraphs separated by blank lines)
                </span>
                <textarea
                  value={activeTranslation.body}
                  onChange={(e) => updateTranslation({ body: e.target.value })}
                  rows={8}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 font-mono text-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-slate-700">Author</span>
                <input
                  value={activeTranslation.authorName || ""}
                  onChange={(e) => updateTranslation({ authorName: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2"
                />
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
          <button
            type="button"
            disabled={busy || uploadBusy}
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || uploadBusy}
            className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {busy ? "Saving…" : "Save post"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogFormModal;
